/**
 * Product Detail Page - Variant Selection Handler
 * Handles button-based variant selection with availability logic
 *
 * Improvement round (2026-07-01):
 * - Size is NOT pre-selected on first load (unless URL names a variant);
 *   Add to Cart (main + sticky) guards until the shopper picks a size.
 * - Gallery/variant sync: falls back to color-NAME and variant featured
 *   media matching when no 3-digit color code is available (fixes
 *   "Selected: Brown" showing a black shoe).
 * - Installment subtext updates with the variant price.
 * - Size Guide dialog wiring.
 * - Back-in-stock capture visibility follows variant availability.
 */

(function() {
  'use strict';
  
  if (!window.productData) {
    console.warn('Product data not found');
    return;
  }

  const productData = window.productData;
  let selectedOptions = {};
  let sizeOptionIndex = -1;
  let widthOptionIndex = -1;
  let userHasSelectedSize = false;
  let sizeErrorTimeout = null;
  
  // Find option indices for Size, Width/Fit, and Color
  let colorOptionIndex = -1;
  productData.options.forEach((optionName, index) => {
    const nameLower = optionName.toLowerCase();
    if (nameLower.includes('color') || nameLower.includes('style') || nameLower.includes('colour')) {
      colorOptionIndex = index;
    } else if (nameLower.includes('size')) {
      sizeOptionIndex = index;
    } else if (nameLower.includes('width') || nameLower.includes('fit')) {
      widthOptionIndex = index;
    }
  });

  // Size pre-selection flag rendered by Liquid (data-preselected="false" when
  // the URL did not explicitly name a variant).
  const sizeWrapEl = document.getElementById('pdp-size-picker-wrap');
  const sizePreselected = !sizeWrapEl || sizeWrapEl.dataset.preselected !== 'false';
  
  // Initialize selected options from current variant
  const currentVariantId = document.getElementById('product-variant-id')?.value;
  let initialVariant = null;
  
  // Wait for DOM to be ready before initializing
  function initializeGallery() {
    if (!productData || !productData.variants || productData.variants.length === 0) {
      console.warn('[PDP] Product data not available, retrying...');
      setTimeout(initializeGallery, 200);
      return;
    }
    
    if (currentVariantId) {
      const currentVariant = productData.variants.find(v => v.id == currentVariantId);
      if (currentVariant) {
        currentVariant.options.forEach((option, index) => {
          selectedOptions[index] = option;
        });
        initialVariant = currentVariant;
      }
    }
    
    // If no variant selected, use first available
    if (!initialVariant) {
      const firstVariant = productData.variants.find(v => v.available) || productData.variants[0];
      if (firstVariant) {
        firstVariant.options.forEach((option, index) => {
          selectedOptions[index] = option;
        });
        initialVariant = firstVariant;
        applySizeSelectionPolicy();
        updateVariant(firstVariant);
        return;
      }
    }

    applySizeSelectionPolicy();

    if (initialVariant) {
      // Update gallery for initial variant if one was already selected
      updateGallery(initialVariant);
    }
  }

  /**
   * If size was not explicitly chosen (no ?variant= in URL), clear the size
   * selection so the shopper must actively pick one.
   */
  function applySizeSelectionPolicy() {
    if (sizeOptionIndex < 0) return;
    if (sizePreselected) {
      userHasSelectedSize = true;
      return;
    }
    userHasSelectedSize = false;
    selectedOptions[sizeOptionIndex] = null;
    const sizePicker = document.querySelector(`.pdp-option-picker[data-option-index="${sizeOptionIndex}"]`);
    if (sizePicker) {
      sizePicker.querySelectorAll('.pdp-option-pill').forEach(btn => {
        btn.classList.remove('pdp-option-pill--selected');
        btn.setAttribute('aria-pressed', 'false');
      });
    }
  }
  
  // Run initialization - wait for both DOM and productData
  function startInitialization() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        setTimeout(initializeGallery, 50);
      });
    } else {
      // DOM ready, but wait a bit for productData script to execute
      setTimeout(initializeGallery, 50);
    }
  }
  
  startInitialization();
  
  function clearSizeErrorState() {
    if (sizeErrorTimeout) {
      clearTimeout(sizeErrorTimeout);
      sizeErrorTimeout = null;
    }
    const sizeWrap = document.getElementById('pdp-size-picker-wrap');
    if (sizeWrap) {
      const picker = sizeWrap.querySelector('.pdp-option-picker');
      if (picker) picker.classList.remove('pdp-option-picker--error');
    }
    const errEl = document.getElementById('pdp-sticky-size-error');
    if (errEl) {
      errEl.setAttribute('hidden', '');
    }
    const hintEl = document.getElementById('pdp-size-hint');
    if (hintEl) {
      hintEl.setAttribute('hidden', '');
    }
  }

  /**
   * Shared "pick a size first" affordance: scroll to the size picker,
   * pulse it, and show the inline hint.
   */
  function showSizeError() {
    clearSizeErrorState();
    const wrap = document.getElementById('pdp-size-picker-wrap');
    if (wrap) {
      wrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const picker = wrap.querySelector('.pdp-option-picker');
      if (picker) picker.classList.add('pdp-option-picker--error');
    }
    const hintEl = document.getElementById('pdp-size-hint');
    if (hintEl) hintEl.removeAttribute('hidden');
    const errEl = document.getElementById('pdp-sticky-size-error');
    if (errEl) errEl.removeAttribute('hidden');
    sizeErrorTimeout = setTimeout(clearSizeErrorState, 2600);
  }

  function needsSizeSelection() {
    return sizeOptionIndex >= 0 && !userHasSelectedSize;
  }

  /**
   * Select an option value
   * @param {number} optionIndex - The option index (0-based)
   * @param {string} value - The option value
   */
  function selectOption(optionIndex, value) {
    if (optionIndex === sizeOptionIndex) {
      userHasSelectedSize = true;
      clearSizeErrorState();
    }
    const previousValue = selectedOptions[optionIndex];
    selectedOptions[optionIndex] = value;
    
    // Update UI - remove active from all buttons in this picker
    const picker = document.querySelector(`[data-option-index="${optionIndex}"]`);
    if (picker) {
      picker.querySelectorAll('.pdp-option-pill').forEach(btn => {
        btn.classList.remove('pdp-option-pill--selected');
        btn.setAttribute('aria-pressed', 'false');
      });
      
      const selectedButton = picker.querySelector(`[data-option-value="${value}"]`);
      if (selectedButton) {
        selectedButton.classList.add('pdp-option-pill--selected');
        selectedButton.setAttribute('aria-pressed', 'true');
      }
      
      // Update "Selected: {color}" label for color picker
      if (optionIndex === colorOptionIndex) {
        var colorLabel = picker.querySelector('.pdp-option-picker__selected-color-text');
        if (colorLabel) colorLabel.textContent = 'Selected: ' + value;
      }
      
      // Update hidden select
      const select = picker.querySelector('.pdp-option-picker__select');
      if (select) {
        select.value = value;
        select.dispatchEvent(new Event('change'));
      }
    }
    
    // Special handling: if width changed, check if current size is still available
    if (optionIndex === widthOptionIndex && sizeOptionIndex >= 0) {
      const currentSize = selectedOptions[sizeOptionIndex];
      if (currentSize) {
        const isSizeAvailable = checkOptionAvailability(sizeOptionIndex, currentSize);
        if (!isSizeAvailable) {
          // Size no longer available for this width - clear it and let the
          // shopper re-pick (never silently switch their size).
          selectedOptions[sizeOptionIndex] = null;
          userHasSelectedSize = false;
          updateUIForUnavailableSize();
        }
      }
    }
    
    // Special handling: if size changed, check if current width is still available
    if (optionIndex === sizeOptionIndex && widthOptionIndex >= 0) {
      const currentWidth = selectedOptions[widthOptionIndex];
      if (currentWidth) {
        const isWidthAvailable = checkOptionAvailability(widthOptionIndex, currentWidth);
        if (!isWidthAvailable) {
          // Find first available width for this size
          const availableWidth = findFirstAvailableWidth();
          if (availableWidth) {
            selectOption(widthOptionIndex, availableWidth);
            return;
          } else {
            selectedOptions[widthOptionIndex] = null;
            updateUIForUnavailableWidth();
          }
        }
      }
    }
    
    // Find matching variant
    const matchingVariant = findMatchingVariant();
    
    if (matchingVariant) {
      updateVariant(matchingVariant);
    } else {
      // No matching variant - disable add to cart
      disableAddToCart('Unavailable');
    }
    
    // Update availability for all option buttons
    updateVariantAvailability();
  }
  
  /**
   * Find matching variant based on selected options
   */
  function findMatchingVariant() {
    const optionValues = [];
    for (let i = 0; i < productData.options.length; i++) {
      optionValues.push(selectedOptions[i] || null);
    }
    
    return productData.variants.find(variant => {
      return variant.options.every((option, index) => {
        return optionValues[index] === null || optionValues[index] === option;
      });
    });
  }
  
  /**
   * Check if an option value is available with current selections
   */
  function checkOptionAvailability(optionIndex, value) {
    const testOptions = { ...selectedOptions };
    testOptions[optionIndex] = value;
    
    const optionValues = [];
    for (let i = 0; i < productData.options.length; i++) {
      optionValues.push(testOptions[i] || null);
    }
    
    return productData.variants.some(variant => {
      if (!variant.available) return false;
      return variant.options.every((option, index) => {
        return optionValues[index] === null || optionValues[index] === option;
      });
    });
  }
  
  /**
   * Find first available width for current size selection
   */
  function findFirstAvailableWidth() {
    if (widthOptionIndex < 0) return null;
    
    const widthPicker = document.querySelector(`[data-option-index="${widthOptionIndex}"]`);
    if (!widthPicker) return null;
    
    const widthButtons = widthPicker.querySelectorAll('.pdp-option-pill:not(.pdp-option-pill--disabled)');
    for (const button of widthButtons) {
      const value = button.dataset.optionValue;
      if (checkOptionAvailability(widthOptionIndex, value)) {
        return value;
      }
    }
    return null;
  }
  
  /**
   * Update UI when size becomes unavailable
   */
  function updateUIForUnavailableSize() {
    const sizePicker = document.querySelector(`[data-option-index="${sizeOptionIndex}"]`);
    if (sizePicker) {
      sizePicker.querySelectorAll('.pdp-option-pill').forEach(btn => {
        btn.classList.remove('pdp-option-pill--selected');
        btn.setAttribute('aria-pressed', 'false');
      });
    }
  }
  
  /**
   * Update UI when width becomes unavailable
   */
  function updateUIForUnavailableWidth() {
    const widthPicker = document.querySelector(`[data-option-index="${widthOptionIndex}"]`);
    if (widthPicker) {
      widthPicker.querySelectorAll('.pdp-option-pill').forEach(btn => {
        btn.classList.remove('pdp-option-pill--selected');
        btn.setAttribute('aria-pressed', 'false');
      });
    }
  }
  
  /**
   * Update gallery based on the selected variant.
   * Matching order:
   *   1. 3-digit color code (media metafield or alt text)
   *   2. Color NAME in media alt text (e.g. "brown")
   *   3. The variant's own featured media
   *   4. All images (last resort)
   */
  function updateGallery(variant) {
    const mainImage = document.getElementById('pdp-main-image');
    
    if (!mainImage || !productData.media) return;
    
    // Get color code from variant metafield (handle both .value and direct access)
    let colorCode = '';
    if (variant.color_code) {
      if (typeof variant.color_code === 'string') {
        colorCode = variant.color_code.trim();
      } else if (variant.color_code.value) {
        colorCode = variant.color_code.value.trim();
      }
    }
    colorCode = String(colorCode).trim();

    // Color NAME of this variant (e.g. "Brown") for fallback matching
    let colorName = '';
    if (colorOptionIndex >= 0 && variant.options && variant.options[colorOptionIndex]) {
      colorName = String(variant.options[colorOptionIndex]).toLowerCase().trim();
    }
    
    let galleryImages = [];
    
    // 1) Filter images by color code
    if (colorCode && productData.media.length > 0) {
      const codeLower = colorCode.toLowerCase().trim();
      
      galleryImages = productData.media.filter(img => {
        // Method 1: Check media.color_code (from metafield)
        if (img.color_code) {
          const imgCode = (typeof img.color_code === 'string' ? img.color_code : img.color_code.value || '').toLowerCase().trim();
          if (imgCode === codeLower) return true;
        }
        
        // Method 2: Check alt_text for color code pattern
        if (img.alt_text) {
          const altText = img.alt_text;
          const threeDigitPattern = /\b(\d{3})\b/g;
          const matches = altText.match(threeDigitPattern);
          
          if (matches) {
            for (const match of matches) {
              if (match.trim() === colorCode.trim() || match.trim() === codeLower) return true;
            }
          }
          
          const altLower = altText.toLowerCase();
          if (altLower.includes(codeLower)) {
            const codePattern = new RegExp(`(^|[^0-9])${codeLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([^0-9]|$)`);
            if (codePattern.test(altLower)) return true;
          }
        }
        
        // Method 3: Check filename if available (fallback)
        if (img.filename) {
          if (img.filename.toLowerCase().includes(codeLower)) return true;
        }
        
        return false;
      });
    }

    // 2) Fallback: match color NAME in alt text
    if (galleryImages.length === 0 && colorName) {
      galleryImages = productData.media.filter(img => {
        const alt = (img.alt_text || img.alt || '').toLowerCase();
        return alt.includes(colorName);
      });
    }

    // 3) Fallback: the variant's own featured media
    if (galleryImages.length === 0 && variant.featured_media) {
      const fm = productData.media.find(m => m.id == variant.featured_media.id);
      if (fm) galleryImages = [fm];
    }

    // 4) Last resort: all images
    if (galleryImages.length === 0) {
      galleryImages = productData.media;
    }
    
    // Main image: prefer the variant's featured media when it's in the set
    let mainCandidate = galleryImages[0];
    if (variant.featured_media) {
      const fmInSet = galleryImages.find(m => m.id == variant.featured_media.id);
      if (fmInSet) mainCandidate = fmInSet;
    }
    if (mainCandidate) {
      mainImage.src = mainCandidate.src;
      mainImage.alt = mainCandidate.alt || '';
      if (mainCandidate.id) {
        mainImage.dataset.mediaId = mainCandidate.id;
      }
    }
    
    // Update desktop thumbnail rail: show/hide by variant color, set active on main candidate
    const gridContainer = document.getElementById('pdp-media-grid');
    if (gridContainer) {
      const gridItems = gridContainer.querySelectorAll('.pdp-media-grid__item');
      const matchingImageIds = galleryImages.map(function(img) { return img.id; });
      const activeId = mainCandidate ? mainCandidate.id : null;
      gridItems.forEach(function(el) {
        var mediaId = parseInt(el.dataset.mediaId, 10);
        var shouldShow = matchingImageIds.indexOf(mediaId) !== -1;
        if (shouldShow) {
          el.style.display = '';
          el.classList.remove('pdp-media-grid__item--hidden');
          if (activeId !== null && mediaId == activeId) {
            el.classList.add('pdp-media-grid__item--active');
            el.setAttribute('aria-selected', 'true');
          } else {
            el.classList.remove('pdp-media-grid__item--active');
            el.setAttribute('aria-selected', 'false');
          }
        } else {
          el.style.display = 'none';
          el.classList.add('pdp-media-grid__item--hidden');
          el.classList.remove('pdp-media-grid__item--active');
        }
      });
    }
  }
  
  /**
   * Update variant (price, availability, image, URL)
   */
  function updateVariant(variant) {
    // Update variant ID
    const variantIdInput = document.getElementById('product-variant-id');
    if (variantIdInput) {
      variantIdInput.value = variant.id;
    }
    
    // Update URL without reload (only when the size has actually been chosen,
    // so shared links don't imply a size the shopper never picked)
    if (!needsSizeSelection()) {
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
    
    // Update price
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    });
    
    const priceHtml = formatter.format(variant.price / 100);
    const priceElement = document.getElementById('pdp-price');
    const stickyPriceElement = document.getElementById('pdp-sticky-price');
    
    if (priceElement) {
      let fullPrice = `<span class="pdp__price-current">${priceHtml}</span>`;
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        const comparePrice = formatter.format(variant.compare_at_price / 100);
        fullPrice += `<span class="pdp__price-compare">${comparePrice}</span>`;
      }
      priceElement.innerHTML = fullPrice;
    }
    
    if (stickyPriceElement) {
      stickyPriceElement.textContent = priceHtml;
    }

    // Update installment subtext (4 interest-free payments of $X)
    const installmentEl = document.getElementById('pdp-installment-amount');
    if (installmentEl) {
      installmentEl.textContent = formatter.format(variant.price / 400);
    }
    
    // Update add to cart button
    const addToCartButton = document.getElementById('pdp-add-to-cart');
    const stickyButton = document.getElementById('pdp-sticky-add-to-cart');
    const needsSize = needsSizeSelection();
    
    if (addToCartButton) {
      if (variant.available) {
        addToCartButton.disabled = false;
        addToCartButton.querySelector('.pdp__button-text').textContent = needsSize ? 'Select Your Size' : 'Add to Cart';
      } else {
        addToCartButton.disabled = true;
        addToCartButton.querySelector('.pdp__button-text').textContent = 'Sold Out';
      }
    }
    
    if (stickyButton) {
      if (variant.available) {
        stickyButton.disabled = false;
        stickyButton.textContent = needsSize ? 'Select Size' : 'Add to Cart';
      } else {
        stickyButton.disabled = true;
        stickyButton.textContent = 'Sold Out';
      }
    }
    
    // Show/hide Schedule In-Store Scan button based on availability
    const scheduleButton = document.querySelector('.pdp__button--primary-outline[href*="schedule"]');
    if (scheduleButton) {
      if (variant.available) {
        scheduleButton.style.display = 'none';
      } else {
        scheduleButton.style.display = 'block';
      }
    }

    // Back-in-stock capture: show only when the selected variant is unavailable
    const bisEl = document.getElementById('pdp-bis');
    if (bisEl) {
      if (variant.available) {
        bisEl.setAttribute('hidden', '');
      } else {
        bisEl.removeAttribute('hidden');
      }
    }

    // Update ship-from messaging (Item 6)
    const shipFrom = document.getElementById('pdp-ship-from');
    if (shipFrom) {
      const loc = shipFrom.getAttribute('data-location') || 'Matthews, NC';
      if (variant.available) {
        shipFrom.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg><span>In stock · Ships within 1 business day from ' + loc + '</span>';
      } else {
        shipFrom.innerHTML = '<span class="pdp__ship-from--oos">Currently out of stock. <a href="tel:7048460301">Call for availability</a></span>';
      }
    }

    // Update gallery based on variant color (code, name, or featured media)
    updateGallery(variant);
    
    // Dispatch single variant:change event for other components to listen
    const variantChangeEvent = new CustomEvent('variant:change', {
      detail: { variant: variant },
      bubbles: true
    });
    document.dispatchEvent(variantChangeEvent);
    
    // Update sticky bar options and thumbnail
    updateStickyBarOptions();
    updateStickyBarThumb(variant);
    
    // Update selected summary line
    updateSelectedSummary();
  }
  
  /**
   * Update selected summary line (e.g., "Selected: Black / Wide / 4.5")
   */
  function updateSelectedSummary() {
    const summaryElement = document.getElementById('pdp-selected-summary');
    const summaryText = document.querySelector('.pdp__selected-text');
    if (!summaryElement || !summaryText) return;
    
    const summaryParts = [];
    productData.options.forEach((optionName, index) => {
      if (selectedOptions[index]) {
        summaryParts.push(selectedOptions[index]);
      }
    });
    
    if (summaryParts.length > 0) {
      summaryText.textContent = `Selected: ${summaryParts.join(' / ')}`;
      summaryElement.style.display = 'block';
    } else {
      summaryElement.style.display = 'none';
    }
  }
  
  /**
   * Initialize hero image: click to zoom; stack item click updates hero
   */
  function initHeroImageZoom() {
    const heroContainer = document.querySelector('.pdp-media__main');
    const heroImage = document.getElementById('pdp-main-image');
    
    if (heroContainer && heroImage) {
      heroContainer.addEventListener('click', function() {
        const fullSizeUrl = heroImage.src.replace(/width=\d+/, 'width=2000');
        window.open(fullSizeUrl, '_blank');
      });
    }
    
    const stack = document.getElementById('pdp-gallery-stack');
    if (stack && heroImage) {
      stack.addEventListener('click', function(e) {
        const item = e.target.closest('.pdp-media__stack-item');
        if (!item) return;
        const img = item.querySelector('.pdp-media__stack-image');
        if (img) {
          heroImage.src = img.src.replace(/width=\d+/, 'width=1200');
          heroImage.alt = img.alt || '';
          if (item.dataset.mediaId) heroImage.dataset.mediaId = item.dataset.mediaId;
          stack.querySelectorAll('.pdp-media__stack-item').forEach(i => i.classList.remove('pdp-media__stack-item--active'));
          item.classList.add('pdp-media__stack-item--active');
        }
      });
    }
  }
  
  /**
   * Update sticky bar with selected options
   */
  function updateStickyBarOptions() {
    const optionsElement = document.getElementById('pdp-sticky-options');
    if (!optionsElement) return;
    
    const options = [];
    productData.options.forEach((optionName, index) => {
      if (selectedOptions[index]) {
        options.push(`${optionName}: ${selectedOptions[index]}`);
      }
    });

    if (needsSizeSelection()) {
      options.push('Size: not selected');
    }
    
    optionsElement.textContent = options.join(' • ') || 'Select options';
  }

  /**
   * Update sticky bar thumbnail and title (Round 2)
   */
  function updateStickyBarThumb(variant) {
    const thumb = document.getElementById('pdp-sticky-thumb');
    const titleEl = document.getElementById('pdp-sticky-title');
    if (thumb && productData) {
      var src = productData.featuredImage || '';
      if (variant && variant.featured_media && variant.featured_media.src) {
        src = variant.featured_media.src;
      }
      if (src) {
        thumb.src = src;
        thumb.alt = variant && variant.featured_media && variant.featured_media.alt ? variant.featured_media.alt : (productData.productTitle || '');
      }
    }
    if (titleEl && productData && productData.productTitle) {
      titleEl.textContent = productData.productTitle;
    }
  }
  
  /**
   * Disable add to cart
   */
  function disableAddToCart(text) {
    const addToCartButton = document.getElementById('pdp-add-to-cart');
    const stickyButton = document.getElementById('pdp-sticky-add-to-cart');
    
    if (addToCartButton) {
      addToCartButton.disabled = true;
      addToCartButton.querySelector('.pdp__button-text').textContent = text || 'Unavailable';
    }
    
    if (stickyButton) {
      stickyButton.disabled = true;
      stickyButton.textContent = text || 'Unavailable';
    }
  }
  
  /**
   * Update availability for all option buttons
   */
  function updateVariantAvailability() {
    document.querySelectorAll('.pdp-option-pill').forEach(button => {
      const value = button.dataset.optionValue;
      const optionIndex = parseInt(button.dataset.optionIndex);
      
      if (value === undefined || isNaN(optionIndex)) return;
      
      const isAvailable = checkOptionAvailability(optionIndex, value);
      
      if (isAvailable) {
        button.classList.remove('pdp-option-pill--disabled');
        button.disabled = false;
        button.setAttribute('aria-label', value);
      } else {
        button.classList.add('pdp-option-pill--disabled');
        button.disabled = true;
        button.setAttribute('aria-label', value + ' (Unavailable)');
      }
    });
  }
  
  /**
   * Initialize sticky bar visibility on scroll + scroll-to-size when no size selected (Round 3)
   */
  function initStickyBar() {
    const stickyBar = document.getElementById('pdp-sticky-bar');
    if (!stickyBar) return;
    
    const formElement = document.getElementById('product-form');
    if (!formElement) return;
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.boundingClientRect.bottom < 0) {
          // Form is above the viewport
          stickyBar.style.display = 'flex';
        } else {
          stickyBar.style.display = 'none';
        }
      });
    }, {
      threshold: 0
    });
    
    observer.observe(formElement);
    
    var stickyBtn = document.getElementById('pdp-sticky-add-to-cart');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', function(e) {
        if (stickyBtn.disabled) return;
        if (needsSizeSelection()) {
          e.preventDefault();
          e.stopPropagation();
          showSizeError();
          return;
        }
        var form = document.getElementById('product-form');
        if (form && form.requestSubmit) form.requestSubmit();
      });
    }
  }

  /**
   * Guard the main Add to Cart: no size chosen -> scroll to size picker
   * instead of adding a default size to the cart.
   */
  function initSizeRequiredGuard() {
    const form = document.getElementById('product-form');
    if (form) {
      form.addEventListener('submit', function(e) {
        if (needsSizeSelection()) {
          e.preventDefault();
          e.stopPropagation();
          showSizeError();
        }
      }, true);
    }
    const addToCartButton = document.getElementById('pdp-add-to-cart');
    if (addToCartButton) {
      addToCartButton.addEventListener('click', function(e) {
        if (needsSizeSelection()) {
          e.preventDefault();
          e.stopPropagation();
          showSizeError();
        }
      }, true);
    }
  }

  /**
   * Size Guide dialog
   */
  function initSizeGuide() {
    var dialog = document.getElementById('pdp-size-guide-dialog');
    if (!dialog || !dialog.showModal) return;
    document.querySelectorAll('[data-open-size-guide]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        dialog.showModal();
      });
    });
    dialog.addEventListener('click', function(e) {
      if (e.target.closest('[data-close-size-guide]')) {
        dialog.close();
        return;
      }
      // Click on the backdrop (outside the inner card) closes
      if (e.target === dialog) dialog.close();
    });
  }
  
  /**
   * Update main image when thumbnail is clicked
   */
  function updateMainImage(src, button, mediaId) {
    const mainImage = document.getElementById('pdp-main-image');
    if (mainImage) {
      mainImage.src = src;
      if (mediaId) {
        mainImage.dataset.mediaId = mediaId;
      }
    }
    
    // Update active thumbnail state
    if (button) {
      document.querySelectorAll('.pdp-media__thumbnail').forEach(t => {
        t.classList.remove('pdp-media__thumbnail--active');
        t.setAttribute('aria-selected', 'false');
      });
      button.classList.add('pdp-media__thumbnail--active');
      button.setAttribute('aria-selected', 'true');
    }
  }
  
  // Make functions globally available
  window.cfwVariantPicker = {
    selectOption: selectOption,
    updateMainImage: updateMainImage
  };
  
  /**
   * Lightbox: <dialog> full-screen, prev/next + counter, keyboard arrows (Round 3)
   */
  function initLightbox() {
    var dialog = document.getElementById('pdp-lightbox-dialog');
    var lightboxImg = document.getElementById('pdp-lightbox-image');
    var currentEl = document.getElementById('pdp-lightbox-current');
    var totalEl = document.getElementById('pdp-lightbox-total');
    if (!dialog || !lightboxImg) return;

    var slides = [];
    var currentIndex = 0;

    function getVisibleSlides() {
      var list = [];
      var grid = document.getElementById('pdp-media-grid');
      if (grid) {
        var items = grid.querySelectorAll('.pdp-media-grid__item:not(.pdp-media-grid__item--hidden)');
        items.forEach(function(item) {
          var img = item.querySelector('img[data-pdp-lightbox]');
          if (img) list.push({ src: img.dataset.fullSrc || img.src, alt: img.alt || '' });
        });
      }
      if (list.length === 0) {
        var track = document.querySelector('.pdp-carousel__track');
        if (track) {
          var slideImgs = track.querySelectorAll('.pdp-carousel__slide img[data-pdp-lightbox]');
          slideImgs.forEach(function(img) { list.push({ src: img.src, alt: img.alt || '' }); });
        }
      }
      if (list.length === 0 && window.productData && window.productData.media) {
        window.productData.media.forEach(function(m) {
          if (m.src) list.push({ src: m.src, alt: m.alt || '' });
        });
      }
      return list;
    }

    function showSlide(index) {
      if (slides.length === 0) return;
      currentIndex = (index + slides.length) % slides.length;
      var s = slides[currentIndex];
      lightboxImg.src = s.src;
      lightboxImg.alt = s.alt;
      if (currentEl) currentEl.textContent = currentIndex + 1;
      if (totalEl) totalEl.textContent = slides.length;
    }

    function openLightbox(clickedImg) {
      slides = getVisibleSlides();
      if (slides.length === 0) return;
      var idx = 0;
      var clickedSrc = clickedImg.dataset.fullSrc || clickedImg.src;
      for (var i = 0; i < slides.length; i++) {
        if (slides[i].src === clickedSrc) { idx = i; break; }
      }
      currentIndex = idx;
      showSlide(currentIndex);
      dialog.showModal();
      dialog.querySelector('[data-pdp-lightbox-close]').focus();
    }

    function closeLightbox() {
      if (dialog.close) dialog.close();
    }

    dialog.addEventListener('click', function(e) {
      if (e.target.closest('[data-pdp-lightbox-close]')) {
        closeLightbox();
        return;
      }
      if (e.target.closest('[data-pdp-lightbox-prev]')) {
        showSlide(currentIndex - 1);
        return;
      }
      if (e.target.closest('[data-pdp-lightbox-next]')) {
        showSlide(currentIndex + 1);
        return;
      }
      if (e.target.classList.contains('pdp-lightbox__backdrop')) closeLightbox();
    });

    dialog.addEventListener('keydown', function(e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        showSlide(currentIndex - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        showSlide(currentIndex + 1);
      }
    });

    document.addEventListener('click', function(e) {
      var img = e.target.closest && e.target.closest('img[data-pdp-lightbox]');
      if (img) {
        e.preventDefault();
        openLightbox(img);
        return;
      }
    });
  }

  // Initialize endorsement popover (Item 3)
  function initEndorsementPopover() {
    var toggle = document.querySelector('.pdp__endorsement-toggle');
    var popover = document.getElementById('pdp-endorsement-popover');
    if (!toggle || !popover) return;

    toggle.addEventListener('click', function(e) {
      e.stopPropagation();
      var isOpen = !popover.hidden;
      popover.hidden = isOpen;
      toggle.setAttribute('aria-expanded', String(!isOpen));
    });

    document.addEventListener('click', function(e) {
      if (!popover.hidden && !e.target.closest('#pdp-endorsement')) {
        popover.hidden = true;
        toggle.setAttribute('aria-expanded', 'false');
      }
    });

    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && !popover.hidden) {
        popover.hidden = true;
        toggle.setAttribute('aria-expanded', 'false');
        toggle.focus();
      }
    });
  }

  // Initialize on load
  function initializeUI() {
    updateVariantAvailability();
    updateStickyBarOptions();
    var currentV = findMatchingVariant();
    if (currentV) updateStickyBarThumb(currentV);
    updateSelectedSummary();
    initStickyBar();
    initSizeRequiredGuard();
    initSizeGuide();
    initMediaScrollBehavior();
    initHeroImageZoom();
    initLightbox();
    initEndorsementPopover();
  }
  
  /**
   * Initialize smooth scroll behavior for media column
   * When scrolling reaches bottom of media column, continue scrolling the page
   */
  function initMediaScrollBehavior() {
    const mediaScroll = document.querySelector('.pdp-media-scroll');
    if (!mediaScroll) return;
    
    let isScrolling = false;
    
    // Handle wheel events for smooth scroll chaining
    mediaScroll.addEventListener('wheel', function(e) {
      const { scrollTop, scrollHeight, clientHeight } = mediaScroll;
      const isAtTop = scrollTop === 0;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1; // 1px tolerance
      
      if (e.deltaY > 0 && isAtBottom) {
        return;
      }
      
      if (e.deltaY < 0 && isAtTop) {
        return;
      }
    }, { passive: true });
    
    // Also handle touch events for mobile/trackpad
    let touchStartY = 0;
    let touchStartScrollTop = 0;
    
    mediaScroll.addEventListener('touchstart', function(e) {
      touchStartY = e.touches[0].clientY;
      touchStartScrollTop = mediaScroll.scrollTop;
    }, { passive: true });
    
    mediaScroll.addEventListener('touchmove', function(e) {
      const touchY = e.touches[0].clientY;
      const deltaY = touchStartY - touchY;
      const { scrollTop, scrollHeight, clientHeight } = mediaScroll;
      const isAtTop = scrollTop === 0;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;
      
      if (deltaY < 0 && isAtBottom) {
        return;
      }
      
      if (deltaY > 0 && isAtTop) {
        return;
      }
    }, { passive: true });
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeUI);
  } else {
    initializeUI();
  }
})();
